import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Stack,
  Paper,
  Divider,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import PercentIcon from '@mui/icons-material/Percent';
import GavelIcon from '@mui/icons-material/Gavel';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { getConfig } from '../config/bundlerConfig';
import { defaultConfig } from '../config/bundlerConfig';
import { useAuth } from '@clerk/clerk-react';
import { useProject } from '../projects/ProjectContext';

const MotionPaper = motion(Paper);

const pillButtonSx = {
  borderRadius: 999,
  px: 2.5,
  minWidth: 128,
  fontWeight: 700,
  textTransform: 'none',
};

const gradientButtonSx = {
  ...pillButtonSx,
  backgroundImage:
    'linear-gradient(90deg, rgba(168, 85, 247, 1) 0%, rgba(99, 102, 241, 1) 45%, rgba(34, 211, 238, 1) 100%)',
  color: '#08090e',
  boxShadow: '0 8px 28px rgba(168, 85, 247, 0.28)',
  '&:hover': {
    backgroundImage:
      'linear-gradient(90deg, rgba(187, 110, 255, 1) 0%, rgba(115, 122, 255, 1) 45%, rgba(56, 225, 255, 1) 100%)',
    boxShadow: '0 10px 32px rgba(168, 85, 247, 0.35)',
  },
  '&.Mui-disabled': {
    backgroundImage: 'none',
    color: alpha('#e9e7ff', 0.45),
  },
};

const actionRowSx = (theme, accentHue) => ({
  position: 'relative',
  p: { xs: 2, sm: 2.5 },
  borderRadius: 3,
  border: `1px solid ${alpha(accentHue, 0.22)}`,
  background: `linear-gradient(135deg, ${alpha(accentHue, 0.08)} 0%, ${alpha(theme.palette.background.paper, 0.4)} 55%, transparent 100%)`,
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    borderColor: alpha(accentHue, 0.35),
    boxShadow: `0 0 0 1px ${alpha(accentHue, 0.12)}`,
  },
});

const TokenOperations = () => {
  const theme = useTheme();
  const { getToken } = useAuth();
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);
  const { currentProjectId, projectSettings } = useProject();

  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(getConfig());

  const [removeLiqPercent, setRemoveLiqPercent] = useState('100');

  useEffect(() => {
    const id = setInterval(() => setConfig(getConfig()), 1500);
    return () => clearInterval(id);
  }, []);

  const apiFetch = useCallback(
    async (path, init = {}) => {
      const token = await getTokenRef.current();
      if (!token) throw new Error('Not signed in');
      if (!currentProjectId) throw new Error('Select a project first');

      const res = await fetch(`${defaultConfig.SERVER_URL.url}${path}`, {
        ...init,
        headers: {
          ...(init.headers || {}),
          Authorization: `Bearer ${token}`,
          'X-Project-Id': currentProjectId,
        },
      });
      const text = await res.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        data = {};
      }
      if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`);
      return data;
    },
    [currentProjectId],
  );

  const resolvedRpcUrl = useMemo(() => (config?.network?.rpcUrl || '').trim(), [config?.network?.rpcUrl]);
  const resolvedRouterAddress = useMemo(() => (config?.network?.routerAddress || '').trim(), [config?.network?.routerAddress]);
  const resolvedTokenAddress = useMemo(
    () =>
      (projectSettings?.token_address || projectSettings?.tokenAddress || '')
        .toString()
        .trim(),
    [projectSettings],
  );

  const tokenOk = Boolean(resolvedTokenAddress);
  const rpcOk = Boolean(resolvedRpcUrl);
  const routerOk = Boolean(resolvedRouterAddress);

  const shortAddr = (a) => {
    const s = (a || '').toString();
    if (s.length < 14) return s || '—';
    return `${s.slice(0, 6)}…${s.slice(-4)}`;
  };

  const handleZeroTaxNoLimits = async () => {
    if (!resolvedTokenAddress) return toast.error('Set token address');
    if (!resolvedRpcUrl) return toast.error('Set RPC URL');
    setLoading(true);
    try {
      await apiFetch('/api/me/launch/zero-tax-no-limits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rpcUrl: resolvedRpcUrl, tokenAddress: resolvedTokenAddress }),
      });
      toast.success('Taxes set to 0%');
    } catch (e) {
      toast.error(e?.message || 'Failed to update taxes/limits');
    } finally {
      setLoading(false);
    }
  };

  const handleRenounce = async () => {
    if (!resolvedTokenAddress) return toast.error('Set token address');
    if (!resolvedRpcUrl) return toast.error('Set RPC URL');
    setLoading(true);
    try {
      const res = await apiFetch('/api/me/launch/renounce', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rpcUrl: resolvedRpcUrl, tokenAddress: resolvedTokenAddress }),
      });
      toast.success(`Ownership renounced (tx ${res?.txHash || 'sent'})`);
    } catch (e) {
      toast.error(e?.message || 'Failed to renounce');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveLiquidity = async () => {
    if (!resolvedTokenAddress) return toast.error('Set token address');
    if (!resolvedRpcUrl) return toast.error('Set RPC URL');
    if (!resolvedRouterAddress) return toast.error('Set router address');
    setLoading(true);
    try {
      const res = await apiFetch('/api/me/launch/remove-liquidity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rpcUrl: resolvedRpcUrl,
          tokenAddress: resolvedTokenAddress,
          routerAddress: resolvedRouterAddress,
          liquidityPercent: removeLiqPercent,
        }),
      });
      toast.success(`Liquidity removed (tx ${res?.txHash || 'sent'})`);
      if (res?.pair) toast.info(`LP pair: ${res.pair}`);
    } catch (e) {
      toast.error(e?.message || 'Failed to remove liquidity');
    } finally {
      setLoading(false);
    }
  };

  const purple = theme.palette.primary.main;
  const cyan = theme.palette.secondary.main;
  const amber = '#f59e0b';
  const danger = theme.palette.error.main;

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto' }}>
      <Box sx={{ mb: 3.5 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 900,
            letterSpacing: 0.6,
            backgroundImage: `linear-gradient(90deg, ${purple} 0%, #6366f1 45%, ${cyan} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Token Operations
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 560 }}>
          Safe, on-chain actions for your deployed token: zero taxes, renounce ownership, and remove LP using your
          platform RPC and router.
        </Typography>
      </Box>

      <MotionPaper
        elevation={0}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        sx={{
          borderRadius: 4,
          overflow: 'hidden',
          border: `1px solid ${alpha(purple, 0.2)}`,
          background: `linear-gradient(165deg, ${alpha(purple, 0.07)} 0%, ${alpha(theme.palette.background.paper, 0.85)} 38%, ${alpha(cyan, 0.04)} 100%)`,
          backdropFilter: 'blur(12px)',
        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            borderBottom: `1px solid ${alpha('#fff', 0.06)}`,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.5,
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 850, letterSpacing: 0.5, color: 'text.secondary' }}>
            Environment
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap>
            <Chip
              size="small"
              label={tokenOk ? `Token ${shortAddr(resolvedTokenAddress)}` : 'No token on project'}
              icon={tokenOk ? <CheckCircleOutlineIcon sx={{ fontSize: 18 }} /> : <ErrorOutlineIcon sx={{ fontSize: 18 }} />}
              sx={{
                fontWeight: 700,
                fontFamily: tokenOk ? 'monospace' : 'inherit',
                borderRadius: 2,
                backgroundColor: tokenOk ? alpha(cyan, 0.12) : alpha(theme.palette.warning.main, 0.14),
                border: `1px solid ${alpha(tokenOk ? cyan : theme.palette.warning.main, 0.28)}`,
              }}
            />
            <Chip
              size="small"
              label={rpcOk ? 'RPC ready' : 'RPC missing'}
              sx={{
                fontWeight: 700,
                borderRadius: 2,
                backgroundColor: rpcOk ? alpha(purple, 0.14) : alpha(theme.palette.error.main, 0.12),
                border: `1px solid ${alpha(rpcOk ? purple : theme.palette.error.main, 0.25)}`,
              }}
            />
            <Chip
              size="small"
              label={routerOk ? 'Router ready' : 'Router missing'}
              sx={{
                fontWeight: 700,
                borderRadius: 2,
                backgroundColor: routerOk ? alpha(purple, 0.1) : alpha(theme.palette.warning.main, 0.12),
                border: `1px solid ${alpha(routerOk ? purple : theme.palette.warning.main, 0.22)}`,
              }}
            />
          </Stack>
        </Box>

        <Stack divider={<Divider flexItem sx={{ borderColor: alpha('#fff', 0.06) }} />} sx={{ p: { xs: 2, sm: 2.5 }, gap: 0 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            sx={actionRowSx(theme, purple)}
          >
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                  background: alpha(purple, 0.15),
                  border: `1px solid ${alpha(purple, 0.28)}`,
                  color: '#e9dfef',
                }}
              >
                <PercentIcon sx={{ opacity: 0.95 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 880, mb: 0.35 }}>
                  Set 0% taxes
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  Pushes taxes to zero and clears limits on-chain using your LaunchToken-compatible contract.
                </Typography>
              </Box>
            </Stack>
            <Button variant="contained" onClick={handleZeroTaxNoLimits} disabled={loading || !tokenOk || !rpcOk} sx={gradientButtonSx}>
              Apply
            </Button>
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            sx={actionRowSx(theme, amber)}
          >
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                  background: alpha(amber, 0.14),
                  border: `1px solid ${alpha(amber, 0.35)}`,
                  color: '#fde68a',
                }}
              >
                <GavelIcon sx={{ fontSize: 22, opacity: 0.95 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 880, mb: 0.35 }}>
                  Renounce ownership
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  Permanent: you lose owner-only controls (fees, trading gates, exclusions). Confirm on-chain before
                  continuing.
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="outlined"
              onClick={handleRenounce}
              disabled={loading || !tokenOk || !rpcOk}
              sx={{
                ...pillButtonSx,
                borderColor: alpha(amber, 0.55),
                color: '#fde68a',
                '&:hover': {
                  borderColor: amber,
                  backgroundColor: alpha(amber, 0.08),
                },
              }}
            >
              Renounce
            </Button>
          </Stack>

          <Stack spacing={2} sx={actionRowSx(theme, danger)}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                  background: alpha(danger, 0.12),
                  border: `1px solid ${alpha(danger, 0.38)}`,
                  color: '#fecaca',
                }}
              >
                <LinkOffIcon sx={{ opacity: 0.95 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 880, mb: 0.35 }}>
                  Remove liquidity
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
                  Unwinds LP for this token via the router. Uses project token plus platform RPC and router addresses.
                  Double-check pool size and percent before confirming.
                </Typography>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                  <TextField
                    label="Liquidity %"
                    value={removeLiqPercent}
                    onChange={(e) => setRemoveLiqPercent(e.target.value)}
                    inputProps={{ inputMode: 'numeric', min: 0, max: 100 }}
                    size="small"
                    sx={{
                      width: { xs: '100%', sm: 200 },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 999,
                      },
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleRemoveLiquidity}
                    disabled={loading || !tokenOk || !rpcOk || !routerOk}
                    sx={{
                      ...pillButtonSx,
                      borderColor: alpha(danger, 0.5),
                      color: '#fecaca',
                      '&:hover': {
                        borderColor: danger,
                        backgroundColor: alpha(danger, 0.1),
                      },
                    }}
                  >
                    Remove LP
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </MotionPaper>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: { xs: 'left', sm: 'center' } }}>
        Connected project uses token from Launch Wizard / settings. Router and RPC follow platform Configuration.
      </Typography>
    </Box>
  );
};

export default TokenOperations;
